import { useEffect } from 'react'
import { initMetaPixel } from '../utils/metaPixel'

const INTERACTIONS = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const

/** Margen tras el evento `load` antes de cargar el pixel por su cuenta. */
const FALLBACK_DELAY_MS = 6000

/**
 * Carga el Meta Pixel completamente fuera de la ruta crítica.
 *
 * Se inicializa en la primera interacción del visitante (un toque, una tecla o
 * el primer scroll) y, si nadie interactúa, unos segundos después de que la
 * página terminó de cargar. Así los ≈70 KiB del script de Facebook nunca
 * compiten con el LCP, y el PageView se sigue registrando en toda visita real.
 */
export function useMetaPixel(): void {
  useEffect(() => {
    let done = false
    let timeout = 0

    const start = () => {
      if (done) return
      done = true
      cleanup()
      initMetaPixel()
    }

    const scheduleFallback = () => {
      timeout = window.setTimeout(() => {
        const idle = (
          window as typeof window & {
            requestIdleCallback?: (cb: () => void) => void
          }
        ).requestIdleCallback
        if (idle) idle(start)
        else start()
      }, FALLBACK_DELAY_MS)
    }

    function cleanup() {
      INTERACTIONS.forEach((event) =>
        window.removeEventListener(event, start, { capture: true }),
      )
      window.removeEventListener('load', scheduleFallback)
      window.clearTimeout(timeout)
    }

    INTERACTIONS.forEach((event) =>
      window.addEventListener(event, start, {
        capture: true,
        once: true,
        passive: true,
      }),
    )

    if (document.readyState === 'complete') scheduleFallback()
    else window.addEventListener('load', scheduleFallback, { once: true })

    return cleanup
  }, [])
}
